import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './patient-condition-check-up.reducer';

export const PatientConditionCheckUp = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const patientConditionCheckUpList = useAppSelector(state => state.patientConditionCheckUp.entities);
  const loading = useAppSelector(state => state.patientConditionCheckUp.loading);
  const totalItems = useAppSelector(state => state.patientConditionCheckUp.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="patient-condition-check-up-heading" data-cy="PatientConditionCheckUpHeading">
        <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.home.title">Patient Condition Check Ups</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/patient-condition-check-up/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.home.createLabel">
              Create new Patient Condition Check Up
            </Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.patientConditionCheckUp.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {patientConditionCheckUpList && patientConditionCheckUpList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('stageType')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.stageType">Stage Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stageType')} />
                </th>
                <th className="hand" onClick={sort('observationSummary')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.observationSummary">Observation Summary</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('observationSummary')} />
                </th>
                <th className="hand" onClick={sort('clinicalStatus')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.clinicalStatus">Clinical Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('clinicalStatus')} />
                </th>
                <th className="hand" onClick={sort('verificationStatus')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.verificationStatus">Verification Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('verificationStatus')} />
                </th>
                <th className="hand" onClick={sort('conditionCategory')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.conditionCategory">Condition Category</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('conditionCategory')} />
                </th>
                <th className="hand" onClick={sort('conditionSeverity')}>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.conditionSeverity">Condition Severity</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('conditionSeverity')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.patientCondition">Patient Condition</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.diagnosisReport">Diagnosis Report</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.vitals">Vitals</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.reportedBy">Reported By</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.encounter">Encounter</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.ipEncounter">Ip Encounter</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {patientConditionCheckUpList.map((patientConditionCheckUp, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/patient-condition-check-up/${patientConditionCheckUp.id}`} color="link" size="sm">
                      {patientConditionCheckUp.id}
                    </Button>
                  </td>
                  <td>{patientConditionCheckUp.active ? 'true' : 'false'}</td>
                  <td>{patientConditionCheckUp.microSec}</td>
                  <td>{patientConditionCheckUp.lastupdated}</td>
                  <td>{patientConditionCheckUp.timestampentry}</td>
                  <td>{patientConditionCheckUp.cursor}</td>
                  <td>{patientConditionCheckUp.stageType}</td>
                  <td>{patientConditionCheckUp.observationSummary}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.ClinicalStatus.${patientConditionCheckUp.clinicalStatus}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.VerificationStatus.${patientConditionCheckUp.verificationStatus}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.ConditionCategory.${patientConditionCheckUp.conditionCategory}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.ConditionSeverity.${patientConditionCheckUp.conditionSeverity}`} />
                  </td>
                  <td>
                    {patientConditionCheckUp.patientCondition ? (
                      <Link to={`/patient-condition/${patientConditionCheckUp.patientCondition.id}`}>
                        {patientConditionCheckUp.patientCondition.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {patientConditionCheckUp.diagnosisReport ? (
                      <Link to={`/lab-prescription/${patientConditionCheckUp.diagnosisReport.id}`}>
                        {patientConditionCheckUp.diagnosisReport.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {patientConditionCheckUp.vitals ? (
                      <Link to={`/vitals/${patientConditionCheckUp.vitals.id}`}>{patientConditionCheckUp.vitals.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {patientConditionCheckUp.reportedBy ? (
                      <Link to={`/doctor/${patientConditionCheckUp.reportedBy.id}`}>{patientConditionCheckUp.reportedBy.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {patientConditionCheckUp.encounter ? (
                      <Link to={`/appointments/${patientConditionCheckUp.encounter.id}`}>{patientConditionCheckUp.encounter.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {patientConditionCheckUp.ipEncounter ? (
                      <Link to={`/admission/${patientConditionCheckUp.ipEncounter.id}`}>{patientConditionCheckUp.ipEncounter.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/patient-condition-check-up/${patientConditionCheckUp.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/patient-condition-check-up/${patientConditionCheckUp.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/patient-condition-check-up/${patientConditionCheckUp.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.patientConditionCheckUp.home.notFound">
                No Patient Condition Check Ups found
              </Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={patientConditionCheckUpList && patientConditionCheckUpList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PatientConditionCheckUp;
